//
// 発注依頼一覧表 表示モーダル
//
// src/components/orderRequestModal.js
//

import React, { useEffect, useState } from "react";
import "../styles/orderRequestModal.css";
import * as XLSX from "xlsx";

function OrderRequestModal({ onClose, currentUser }) {
  const [orders, setOrders] = useState([]);
  const [userList, setUserList] = useState([]);
  const [selected, setSelected] = useState({}); // 削除チェックボタンの状態
  const [confirmConfig, setConfirmConfig] = useState(null); // 確認モーダル用

// 登録日時 (日本時間2行)
  const formatTimestamp = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    if (isNaN(d)) return ts;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return (
      <>
        <div>{`${yyyy}/${mm}/${dd}`}</div>
        <div>{`${hh}:${min}:${ss}`}</div>
      </>
    );
  };

  //「エクセル出力」ボタンを押したぞ！の挙動 --------------------------------------------
  const handleExportExcel = () => {
    if (!orders.length) {
      setConfirmConfig({
        message: "発注依頼データがありません",
        onConfirm: () => setConfirmConfig(null),
      });
      return;
    }

    // 表形式に変換
    const data = orders.map((o) => {
      const user = userList.find((u) => String(u.id) === String(o.userId));
      return {
        入荷: o.inStok === 1 ? "済" : "未",
        入荷予定: o.scheduleDay || "",
        問屋発注: o.orderDay || "",
        登録日時: o.timestamp,
        発注依頼者: user?.name || o.userId,
        旧棚番: o.oldShelfNumber,
        品名: o.name,
        品番: o.productId,
        発注数: o.orderQuantity,
        単位: o.unit,
        問屋: o.wholesaler || "",
      };
    });

    // ワークシート作成
    const header = [
      "入荷","入荷予定","問屋発注","登録日時",
      "発注依頼者","旧棚番","品名","品番",
      "発注数","単位","問屋"
    ];
    const ws = XLSX.utils.aoa_to_sheet([header]);
    XLSX.utils.sheet_add_json(ws, data, { skipHeader: true, origin: "A2" });

    // 列幅を調整
    ws["!cols"] = [
      { wpx: 30 },   // 入荷
      { wpx: 53 },   // 入荷予定
      { wpx: 53 },   // 問屋発注
      { wpx: 110 },  // 登録日時
      { wpx: 96 },   // 発注依頼者
      { wpx: 53 },   // 旧棚番
      { wpx: 120 },   // 品名
      { wpx: 120 },   // 品番
      { wpx: 48 },   // 発注数
      { wpx: 48 },   // 単位
      { wpx: 64 },   // 問屋
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "発注依頼");

    // ファイル名を生成
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const filename = `発注依頼_${yyyy}-${mm}-${dd}.xlsx`;

    // 出力（保存ダイアログが開く）
    XLSX.writeFile(wb, filename);
  };

  //「変更登録」ボタンを押したぞ！の挙動 -----------------------------------------------
  const handleSaveChanges = async () => {
    setConfirmConfig({
      message: "変更を登録します。よろしいですか？",
      onConfirm: async () => {
        try {
          const res = await fetch("https://newells4610.stars.ne.jp/api/update_order_request.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ orders }),
          });
          const json = await res.json();
          if (!res.ok || json.status !== "ok") throw new Error(json.message || "保存に失敗しました");

          // 保存後にデータを再読み込み
          const res2 = await fetch("https://newells4610.stars.ne.jp/data/orderRequestData.json", { cache: "no-store" });
          setOrders(await res2.json());
          
          setConfirmConfig({
            message: "変更を登録できました！✨",
            onConfirm: () => setConfirmConfig(null),
          });
          
        } catch (err) {
          setConfirmConfig({ message: `保存エラー: ${err.message}`, onConfirm: () => setConfirmConfig(null) });
        }
      },
      onCancel: () => setConfirmConfig(null),
    });
  };

  // 「チェックした項目を削除」ボタンを押したぞ！の挙動 ----------------------------------
  const handleDeleteSelected = async () => {

    // 削除対象を timestamp + productId + orderQuantity + oldShelfNumber で一意に特定
    const rowsToDelete = orders
      .filter((order) =>
        selected[
          order.timestamp +
          "_" + order.productId +
          "_" + order.orderQuantity +
          "_" + order.oldShelfNumber
        ]
      )
      .map((order) => ({
        timestamp: order.timestamp,
        productId: order.productId,
        orderQuantity: order.orderQuantity,
        oldShelfNumber: order.oldShelfNumber,
      }));

    if (rowsToDelete.length === 0) {
      setConfirmConfig({
        message: "削除する項目を選択してください！",
        onConfirm: () => setConfirmConfig(null),
      });
      return;
    }

    setConfirmConfig({
      message: "チェックした項目を削除します。本当にいいですか？",
      onConfirm: async () => {
        try {
          const res = await fetch("https://newells4610.stars.ne.jp/api/delete_order_request.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ rows: rowsToDelete }),
          });
          const json = await res.json();
          if (!res.ok || json.status !== "ok") throw new Error(json.message || "削除失敗");
          
          // 削除後にデータを再読み込み
          const res2 = await fetch("https://newells4610.stars.ne.jp/data/orderRequestData.json", { cache: "no-store" });
          setOrders(await res2.json());
          setSelected({});
          setConfirmConfig({
            message: "チェック項目を削除しました！✨",
            onConfirm: () => setConfirmConfig(null),
          });
        } catch (err) {
          setConfirmConfig({ message: `削除エラー: ${err.message}`, onConfirm: () => setConfirmConfig(null) });
        }
      },
      onCancel: () => setConfirmConfig(null),
    });
  };


  //発注依頼データ読み込み -------------------------------------------------------------------
  useEffect(() => {
    fetch("https://newells4610.stars.ne.jp/data/orderRequestData.json", {
      cache: "no-store",
    })
      .then((res) => res.json())
      .then((data) => {
        // timestamp（登録日時）が新しい順にソート
        const sorted = [...data].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setOrders(sorted);
      })
      .catch((err) => {
        console.error("発注依頼データ読み込み失敗:", err);
        setOrders([]);
      });
  }, []);

  //ユーザーリスト読み込み --------------------------------------------------------------------
  useEffect(() => {
    fetch("https://newells4610.stars.ne.jp/data/userList.json", {
      cache: "no-store",
    })
      .then((res) => res.json())
      .then((data) => setUserList(data))
      .catch((err) => {
        console.error("ユーザーリスト読み込み失敗:", err);
        setUserList([]);
      });
  }, []);

  const toggleInStock = (index) => {
    const newOrders = [...orders];
    newOrders[index].inStok = newOrders[index].inStok === 1 ? 0 : 1;
    setOrders(newOrders);
  };


  // ----- 画面 ----------------------------------------------------------------------------

  return (
    <div className="modal-overlay">
      <div className="modal-box large">
        <h4 className="modal-title">発注依頼一覧</h4>

        <div className="table-wrapper">
          <table className="order-table">
            <thead>
              <tr>
                <th>削除</th>
                <th>入荷</th>
                <th>入荷予定</th>
                <th>問屋発注</th>
                <th>登録日時</th>
                <th>発注依頼者</th>
                <th>旧棚番</th>
                <th>品名</th>
                <th>品番</th>
                <th>発注数</th>
                <th>単位</th>
                <th>問屋</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((row, idx) => (
                <tr key={idx}>
                  <td>
                    <input
                      type="checkbox"
                        checked={!!selected[
                        row.timestamp +
                        "_" + row.productId +
                        "_" + row.orderQuantity +
                        "_" + row.oldShelfNumber
                      ]}
                      onChange={(e) =>
                        setSelected({
                          ...selected,
                          [row.timestamp +
                            "_" + row.productId +
                            "_" + row.orderQuantity +
                            "_" + row.oldShelfNumber]: e.target.checked,
                        })
                      }
                    />
                  </td>

                  <td
                    className={
                      row.inStok === 1 ? "instock-yes" : "instock-no"
                    }
                    onClick={() => toggleInStock(idx)}
                  >
                    {row.inStok === 1 ? "済" : "未"}
                  </td>
                  
                  <td>
                    <input
                      type="text"
                      value={row.scheduleDay}
                      placeholder="YY/MM/DD"
                      onChange={(e) => {
                        const newOrders = [...orders];
                        newOrders[idx].scheduleDay = e.target.value;
                        setOrders(newOrders);
                      }}
                    />
                  </td>

                  <td>
                    <input
                      type="text"
                      value={row.orderDay}
                      placeholder="YY/MM/DD"
                      onChange={(e) => {
                        const newOrders = [...orders];
                        newOrders[idx].orderDay = e.target.value;
                        setOrders(newOrders);
                      }}
                    />
                  </td>

                  <td>{formatTimestamp(row.timestamp)}</td>

                  <td>
                    {userList.find((u) => String(u.id) === String(row.userId))
                      ?.name || row.userId}
                  </td>
                  <td>{row.oldShelfNumber}</td>
                  <td className="name-col">{row.name}</td>
                  <td>{row.productId}</td>
                  <td>{row.orderQuantity}</td>
                  <td>{row.unit}</td>
                  <td>{row.wholesaler}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="modal-actions">
          <button
            onClick={() => {
              if (currentUser?.userId !== "KDU93103001000") {
                setConfirmConfig({
                  message: "この機能は利用できません。",
                  onConfirm: () => setConfirmConfig(null),
                });
                return;
              }
              handleDeleteSelected();
            }}
            style={{ background: "#faa" }}
          >
            チェックした項目の削除
          </button>
          
          <button
            onClick={() => {
              if (currentUser?.userId !== "KDU93103001000") {
                setConfirmConfig({
                  message: "この機能は利用できません。",
                  onConfirm: () => setConfirmConfig(null),
                });
                return;
              }
              handleExportExcel();
            }}
          >
            エクセル出力
          </button>
          
          <button
            onClick={() => {
              if (currentUser?.userId !== "KDU93103001000") {
                setConfirmConfig({
                  message: "この機能は利用できません。",
                  onConfirm: () => setConfirmConfig(null),
                });
                return;
              }
              handleSaveChanges();
            }}
            style={{ background: "#aff" }}
          >
            変更登録
          </button>
          
          <button onClick={onClose}>閉じる</button>
          
        </div>
      </div>

      //確認モーダル
      {confirmConfig && (
        <div className="modal-overlay">
          <div className="modal-box small">
            <p>{confirmConfig.message}</p>
            <div className="modal-actions center">
              <button onClick={confirmConfig.onConfirm}>OK</button>
              {confirmConfig.onCancel && <button onClick={confirmConfig.onCancel}>キャンセル</button>}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default OrderRequestModal;
